import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { Account } from './account';
import { AdministratorRole } from './administrator-role';
import { Class } from './class';
import { ClassReport } from './class-report';
import { Course } from './course';
import { NewAccount } from './new-account';
import { Organization } from './organization';
import { OrganizationDetail, OrganizationStats, OrganizationClassData } from './organization-detail';
import { Session } from './session';
import { Student } from './student';
import { SpeechRecArtifact } from './student-artifact';
import { CreatedArtifactMetadata, DaisyChainConversationArtifact } from '../data/daisychain';
import { CourseProgressSummary, UnitProgressDetail } from './student-report';
import { Teacher } from './teacher';
import { ClassUsageLimits } from './class-usage-limits'
import { VisibleAccount } from './visible-account';
import { GeneratedArtifact } from './artifact';
import { ParentAccount } from './parent-account';
import { ParentSignUpRequest } from './parent-signup-account';
import { StudentUsageLimits } from './student-usage-limits';
import { ActivityListItem } from './activity-list-item';
import { Activity, ActivitySequence, ClassAssignment } from './class-assignment';
import { CompletedActivity, StudentAssignmentReport, StudentCompletedCourseActivities } from './student-completed-activities';
import { StudentActivityReport } from './student-activity-report';
import { OrgUsageReport } from './org-usage-report';
import { UsageLimits } from './usage-limits';
import { CertificateEmail } from './certificate-email';
import { StudentLevelReassignment } from './student-level-reassignment';
import { AssignmentReport } from './assignment-report';
import { AssignmentSummary } from './assignments-summary';
import { Assessment, StudentAssessmentDetails } from './assessments';
import { AssignedStudent, AssignmentDetails } from './assignment-details';
import { AssignmentActivity, AssignmentActivityCategory, AssignmentActivityUnit } from './assignment-activities';
import { EEUReport } from './eeu-report';
import { Deal } from './deal';
import { OnboardingStatus } from './onboarding-status';
import { OrgImportData } from './org-import';
import { SupportEmail } from './support-email';
import { CourseContent } from './CourseContent';
import { Roster } from './roster';
import { SyncLog } from './sync-log';
import { DailyActivityReport } from './daily-activity-report';
import { EmailSubscriptionSetting } from './email-subscriptions';
import { StudentQRCode } from './student-qr-code';
import { KadyChainConversation, KadyChainConversationListHolder } from './kadychain';
import { AccountSSOToken } from './account-sso-token';
import { StudentSpeakingScore, UnitSparcScore, DateSparcScore } from './speaking-score';
import { SalesRep } from './sales-rep';
import { PurchasedLicensesInfo } from './purchased-licenses-info';
import { WidaPredictionDetails } from './wida-prediction';
import { StudentDomainScore } from './student-domain-scores';


@Injectable()
export class DataService {
  adminRoles: AdministratorRole[];

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private router: Router
  ) { }

  get currentUserID(): number {
    return this.authService.session.ID;
  }

  filterObject(obj: any, filter: (key: string, value: any) => boolean) {
    return obj === null || obj === undefined ? obj
      : Object.entries(obj)
        .filter(e => filter(e[0], e[1]))
        .reduce((o, v) => { o[v[0]] = v[1]; return o; }, {});
  }

  getHttpOptions(args: { [param: string]: string | string[]; } = undefined) {
    return {
      params: this.filterObject(args, (k, v) => v !== undefined),
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': this.authService.getToken(),
      })
    }
  }

  checkForErrorInResponse(response): void {
    console.log('response: ', response);
    var errorMessage = response && response['errorMessage'] || null;
    if (errorMessage) {
      if (errorMessage.indexOf('[NotFound]') != -1) {
        this.router.navigate(['/']);
      }
      else {
        throw new Error(errorMessage);
      }
    }
  }

  addStudentToParent(parentAccountId: number, studentId: number): Observable<any> {
    var data = {
      StudentID: studentId,
      ParentAccountID: parentAccountId
    }
    return this.http.post(environment.API_ENDPOINT + `/parent/${parentAccountId}/student/${studentId}`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Account>('createAccount'))
    );
  }

  createAccount(account: NewAccount): Observable<Account> {
    return this.http.post<Account>(environment.API_ENDPOINT + '/account', account, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Account>('createAccount'))
    );
  }

  createAccountSession(username: string, password: string): Observable<Session> {
    return this.http.post<Session>(environment.API_ENDPOINT + '/account-session', { Email: username, Password: password })
      .pipe(
        tap(session => {
          session.ExpirationDate = new Date(session.Expiration);
          this.checkForErrorInResponse(session);
        }),
        catchError(this.handleError<Session>('createAccountSession'))
      );
  }

  createAccountSessionWithToken(token: string): Observable<Session> {
    return this.http.post<Session>(environment.API_ENDPOINT + '/clever-session', { Token: token })
      .pipe(
        tap(session => {
          session.ExpirationDate = new Date(session.Expiration);
          this.checkForErrorInResponse(session);
        }),
        catchError(this.handleError<Session>('createAccountSession'))
      );
  }

  getAccountSSOToken(): Observable<AccountSSOToken> {
    let accountID = this.authService.session.ID;

    return this.http.get<AccountSSOToken>(environment.API_ENDPOINT + `/account/${accountID}/sso-token`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AccountSSOToken>('getAccountSSOToken'))
    );
  }

  createClass(classSectionId: number, startingOn: string, ending: string, loginCode: string): Observable<Class> {
    var data = {
      ClassSectionID: classSectionId,
      StartingOn: startingOn,
      Ending: ending,
      LoginCode: loginCode
    }
    return this.http.post<Class>(environment.API_ENDPOINT + '/class', data, this.getHttpOptions())
      .pipe(
        tap(r => {
          this.checkForErrorInResponse(r);
          r.StartingOn = r.StartingOn.split('T')[0];
        }),
        catchError(this.handleError<Class>('createClass'))
      );
  }

  upsertClassAssignment(assignment: AssignmentDetails) {
    return this.http.post<ClassAssignment>(environment.API_ENDPOINT + '/assignment', assignment, this.getHttpOptions())
      .pipe(
        tap(r => { this.checkForErrorInResponse(r) }),
        catchError(this.handleError<ClassAssignment>('upsertClassAssignment'))
      );
  }

  deleteClassAssignment(organizationId: number, assignmentId: number) {
    return this.http.delete(environment.API_ENDPOINT + '/organization/' + organizationId + '/assignments/' + assignmentId, this.getHttpOptions())
      .pipe(
        tap(r => { this.checkForErrorInResponse(r) }),
        catchError(this.handleError('deleteClassAssignment'))
      );
  }

  updateAssignmentSummary(organizationId: number, assignment: AssignmentSummary) {
    let data = {
      StartDate: assignment.StartDate,
      EndDate: assignment.EndDate,
      Name: assignment.Name
    }
    return this.http.post<AssignmentSummary>(environment.API_ENDPOINT + '/organization/' + organizationId + '/assignments/' + assignment.ID, data, this.getHttpOptions())
      .pipe(
        tap(r => { this.checkForErrorInResponse(r) }),
        catchError(this.handleError('updateAssignmentSummary'))
      );
  }

  createOrganization(organization: OrganizationDetail) {
    return this.http.post<Organization>(environment.API_ENDPOINT + '/organization', organization, this.getHttpOptions())
      .pipe(
        tap(r => this.checkForErrorInResponse(r)),
        catchError(this.handleError<Organization>('createSubOrganization'))
      );
  }

  createParentAccount(newAccount: ParentSignUpRequest) {
    return this.http.post<ParentSignUpRequest>(environment.API_ENDPOINT + '/parent/signup', newAccount, this.getHttpOptions())
      .pipe(
        tap(r => this.checkForErrorInResponse(r)),
        catchError(this.handleLoginError<ParentSignUpRequest>('createParentAccount'))
      );
  }

  createStudent(name: string, externalId: string, courseCode: string, classId?: number, parentAccountId?: number, gradeLevel?: string, trialDays?: number): Observable<Student> {
    var data = {
      Name: name,
      ExternalID: externalId,
      CourseCode: courseCode,
      TrialDays: trialDays === 0 ? null : trialDays,
      PassCode: '',
      StayLoggedIn: true,
      ClassID: classId,
      GradeLevel: gradeLevel,
      ParentAccountID: parentAccountId
    };
    return this.http.post<Student>(environment.API_ENDPOINT + '/student', data, this.getHttpOptions())
      .pipe(
        tap(r => this.checkForErrorInResponse(r)),
        catchError(this.handleError<Student>('createStudent'))
      );
  }

  deleteAccount(accountId): Observable<Object> {
    return this.http.delete(environment.API_ENDPOINT + `/account/${accountId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`deleteAccount`))
    );
  }

  deleteClass(classId): Observable<Object> {
    return this.http.delete(environment.API_ENDPOINT + `/class/${classId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`deleteClass`))
    );
  }

  deleteOrganization(organizationId): Observable<Object> {
    return this.http.delete(environment.API_ENDPOINT + `/organization/${organizationId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`deleteOrganization`))
    );
  }

  deleteOrganizationAdministrator(organizationId, accountId): Observable<Object> {
    return this.http.delete(environment.API_ENDPOINT + `/organization/${organizationId}/account/${accountId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`deleteOrganizationAdministrator`))
    );
  }

  deleteStudent(studentId): Observable<Object> {
    return this.http.delete(environment.API_ENDPOINT + `/student/${studentId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`deleteStudent`))
    );
  }

  getAccounts(term: string, selectedSort: string, asc: boolean): Observable<Account[]> {
    return this.http.get<Account[]>(environment.API_ENDPOINT + '/account', this.getHttpOptions({ term: term ? term : undefined, orderBy: selectedSort, asc: String(asc) })).pipe(
      tap(
        account => {
          this.checkForErrorInResponse(account);
          account.forEach(a => a.ExpirationDate ? a.ExpirationDate = new Date(a.ExpirationDate) : null);
        }
      ),
      catchError(this.handleError<Account[]>(`getAccounts`))
    );
  }

  getAccount(id: number): Observable<Account> {
    return this.http.get<Account>(environment.API_ENDPOINT + `/account/${id}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Account>(`getAccount`))
    );
  }

  getOrganizationActiveLicenses(id: number, asOfDate?: string): Observable<PurchasedLicensesInfo[]> {
    const options = this.getHttpOptions({
      asOfDate: asOfDate
    });
    return this.http.get<PurchasedLicensesInfo[]>(environment.API_ENDPOINT + `/organization/${id}/active-licenses`, asOfDate ? options : this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<PurchasedLicensesInfo[]>(`getOrganizationActiveLicenses`))
    );
  }

  getOrganizationAllLicenses(id: number, asOfDate?: string): Observable<PurchasedLicensesInfo[]> {
    const options = this.getHttpOptions({
      asOfDate: asOfDate
    });
    return this.http.get<PurchasedLicensesInfo[]>(environment.API_ENDPOINT + `/organization/${id}/all-licenses`, asOfDate ? options : this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<PurchasedLicensesInfo[]>(`getOrganizationAllLicenses`))
    );
  }

  getOrganizationSalesRep(id: number): Observable<SalesRep> {
    return this.http.get<SalesRep>(environment.API_ENDPOINT + `/organization/${id}/sales-rep`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<SalesRep>('getOrganizationSalesRep'))
    );
  }

  upsertPurchasedLicenses(license: PurchasedLicensesInfo): Observable<PurchasedLicensesInfo> {
    return this.http.post<PurchasedLicensesInfo>(environment.API_ENDPOINT + '/purchased-licenses', license, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<PurchasedLicensesInfo>('upsertPurchasedLicenses'))
    );
  }

  createOrUpdateOrganizationSalesRep(id: number, details: SalesRep): Observable<SalesRep> {
    return this.http.post<SalesRep>(environment.API_ENDPOINT + `/organization/${id}/sales-rep`, details, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<SalesRep>('createOrUpdateOrganizationSettings'))
    );
  }

  getAccountOrganizations(accountId: number): Observable<Organization[]> {
    return this.http.get<Organization[]>(environment.API_ENDPOINT + `/account/${accountId}/organization`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Organization[]>(`getAccounts`))
    );
  }

  getAdministratorRoles(): Observable<AdministratorRole[]> {
    if (!this.adminRoles) {
      var url = environment.API_ENDPOINT + '/administrator-role';
      var options = this.getHttpOptions();
      return this.http.get<AdministratorRole[]>(url, options).pipe(
        tap(r => {
          this.checkForErrorInResponse(r);
          this.adminRoles = r;
        }),
        catchError(this.handleError<AdministratorRole[]>(`getOrganizations`))
      );
    }
    else {
      return of(this.adminRoles);
    }
  }
  getArtifactString(studentID, artifactid, startedActivityID = null): Observable<string> {
    var url = `/student/${studentID}/artifact/${artifactid}`;
    if (startedActivityID) url += `?startedActivity="${startedActivityID}"`;
    return this.http.get<string>(environment.API_ENDPOINT + url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<string>(`getArtifactString`))
    );
  }
  getArtifact(studentID, artifactid, startedActivityID = null): Observable<any> {
    var url = `/student/${studentID}/artifact/${artifactid}`;
    if (startedActivityID) url += `?startedActivity="${startedActivityID}"`;
    return this.http.get<GeneratedArtifact>(environment.API_ENDPOINT + url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<GeneratedArtifact>(`getArtifact`))
    );
  }

  getArtifactCreationMetadata(studentID, artifactid): Observable<CreatedArtifactMetadata[]> {
    var url = `/student/${studentID}/artifact/${artifactid}/creation-metadata`;
    return this.http.get<CreatedArtifactMetadata[]>(environment.API_ENDPOINT + url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<CreatedArtifactMetadata[]>(`getArtifactCreationMetadata`))
    );
  }

  getKadyChainGuidHolder(studentID, artifactid, startedActivityID = null): Observable<KadyChainConversationListHolder> {
    var url = `/student/${studentID}/artifact/${artifactid}`;
    if (startedActivityID) url += `?startedActivity="${startedActivityID}"`;
    return this.http.get<KadyChainConversationListHolder>(environment.API_ENDPOINT + url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<KadyChainConversationListHolder>(`getArtifactString`))
    );
  }

  getKadyChainConversationArtifact(studentID, artifactid, startedActivityID = null): Observable<KadyChainConversation> {
    var url = `/student/${studentID}/artifact/${artifactid}`;
    if (startedActivityID) url += `?startedActivity="${startedActivityID}"`;
    return this.http.get<KadyChainConversation>(environment.API_ENDPOINT + url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<KadyChainConversation>(`getArtifact`))
    );
  }

  getClassAssignments(organizationId: number): Observable<AssignmentSummary[]> {
    return this.http.get<AssignmentSummary[]>(environment.API_ENDPOINT + `/organization/${organizationId}/assignments`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AssignmentSummary[]>(`getClassAssignments`))
    );
  }

  getClassAssignmentDetails(organizationId: number, assignmentID: number): Observable<AssignmentDetails> {
    return this.http.get<AssignmentDetails>(environment.API_ENDPOINT + `/organization/${organizationId}/assignments/${assignmentID}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AssignmentDetails>(`getClassAssignmentDetails`))
    );
  }

  getClassAssignmentReport(organizationId: number, assignmentID: number): Observable<AssignmentReport> {
    return this.http.get<AssignmentReport>(environment.API_ENDPOINT + `/organization/${organizationId}/assignments/${assignmentID}/progress`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AssignmentReport>(`getClassAssignmentReport`))
    );
  }

  getAssessments(): Observable<Assessment[]> {
    return this.http.get<Assessment[]>(environment.API_ENDPOINT + `/assessments`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Assessment[]>(`getAssessments`))
    );
  }

  getClassAssessmentResults(classID: number): Observable<StudentAssessmentDetails[]> {
    return this.http.get<StudentAssessmentDetails[]>(environment.API_ENDPOINT + `/class/${classID}/assessment-results`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentAssessmentDetails[]>(`getClassAssessmentResults`))
    );
  }

  requestAssessment(assessmentID: number, studentIDs: number[]): Observable<any> {
    var data = {
      StudentIDs: studentIDs,
      AssessmentID: assessmentID
    }
    return this.http.post(environment.API_ENDPOINT + '/assessment-request', data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('requestAssessment'))
    );
  }

  getRecentArtifactsMeta(studentID: number, courseCode: string, count?: number, skip?: number): Observable<GeneratedArtifact[]> {
    let options = this.getHttpOptions({
      courseCode: courseCode,
      count: count && count.toString(),
      skip: skip && skip.toString()
    })
    return this.http.get<GeneratedArtifact[]>(
      environment.API_ENDPOINT + `/student/${studentID}/recent-artifacts`, options).pipe(
        tap(r => this.checkForErrorInResponse(r)),
        catchError(this.handleError<GeneratedArtifact[]>(`getArtifacts`))
      );
  }

  // Placeholder function, should never find data
  getPlacementCertificates(studentID, courseCode, startedActivityID = null): Observable<any> {
    return this.getArtifact(studentID, 'ba882f59-b402-427f-97a7-f6ec27d44f26', startedActivityID)
  }

  getClass(id): Observable<Class> {
    return this.http.get<Class>(environment.API_ENDPOINT + `/class/${id}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`getClass`))
    );
  }

  getCurrentClassForStudent(studentId): Observable<Class> {
    return this.http.get<Class>(environment.API_ENDPOINT + `/class-for-student/${studentId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`getClassForStudent`))
    );
  }

  getClassByLoginCode(loginCode: string): Observable<Class> {
    return this.http.get<Class>(environment.API_ENDPOINT + `/class/by-login-code/${loginCode}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class>(`getClass`))
    );
  }

  getClassReport(id, startDate?, endDate?): Observable<ClassReport> {
    var url = environment.API_ENDPOINT + `/class/${id}/report`;
    var options = this.getHttpOptions({
      startDate: startDate,
      endDate: endDate
    });
    return this.http.get<ClassReport>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<ClassReport>(`getClassReport`))
    );
  }

  getDailyActivityReport(classId, startDate, days): Observable<DailyActivityReport[]> {
    var url = environment.API_ENDPOINT + `/class/${classId}/activity-report`;
    var options = this.getHttpOptions();
    return this.http.post<DailyActivityReport[]>(url, { startDate: startDate, days: days }, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<DailyActivityReport[]>('getDailyActivityReport'))
    );
  }

  getEmailSubscriptions(teacherAccountID: number): Observable<EmailSubscriptionSetting[]> {
    return this.http.get<EmailSubscriptionSetting[]>(environment.API_ENDPOINT + `/account/${teacherAccountID}/email-subscriptions`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<EmailSubscriptionSetting[]>('getEmailSubscriptions'))
    );
  }

  getClassEmailSubscriptionSettings(classID: number): Observable<EmailSubscriptionSetting[]> {
    return this.http.get<EmailSubscriptionSetting[]>(environment.API_ENDPOINT + `/class/${classID}/email-subscriptions`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<EmailSubscriptionSetting[]>('getClassEmailSubscriptionSettings'))
    );
  }

  setEmailSubscription(teacherAccountID: number, subscriptionSetting: EmailSubscriptionSetting): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + `/account/${teacherAccountID}/class-email-subscriptions`, subscriptionSetting, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('setEmailSubscription'))
    );
  }

  getClasses(classSectionId?: number): Observable<Class[]> {
    var url = environment.API_ENDPOINT + '/class/';
    var options = this.getHttpOptions({ classSectionId: (classSectionId ? classSectionId.toString() : undefined) });
    return this.http.get<Class[]>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class[]>(`getClasses`))
    );
  }

  getCourseContent(courseCode: string): Observable<CourseContent> {
    var url = environment.API_ENDPOINT + '/course/' + courseCode;
    var options = this.getHttpOptions();
    return this.http.get<CourseContent>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<CourseContent>(`getCourseContent`))
    );
  }

  getCourseProgressByStudent(studentID: number, courseCode: string): Observable<StudentCompletedCourseActivities> {
    var url = environment.API_ENDPOINT + '/student/' + studentID + '/completed-activities/coursecode/' + courseCode;
    var options = this.getHttpOptions();
    return this.http.get<StudentCompletedCourseActivities>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentCompletedCourseActivities>(`getCourseProgressByStudent`))
    );
  }

  getCourses(): Observable<Course[]> {
    var url = environment.API_ENDPOINT + '/course';
    var options = this.getHttpOptions();
    return this.http.get<Course[]>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Course[]>(`getCourses`))
    );
  }

  getClassUsageLimits(classId: number): Observable<ClassUsageLimits> {
    return this.http.get<ClassUsageLimits>(environment.API_ENDPOINT + `/class/${classId}/usage-limit`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<ClassUsageLimits>('getDailyUsageLimit'))
    );
  }

  getDomain(): string {
    return location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
  }

  getOrganizations(term?: string): Observable<Organization[]> {
    var url = environment.API_ENDPOINT + '/organization/';
    var options = this.getHttpOptions();
    if (term) {
      options["params"] = { term: term }
    }
    return this.http.get<Organization[]>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Organization[]>(`getOrganizations`))
    );
  }

  getOrganizationDetail(organizationId: number): Observable<OrganizationDetail> {
    var url = environment.API_ENDPOINT + `/organization/${organizationId}`;
    return this.http.get<OrganizationDetail>(url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OrganizationDetail>(`getOrganizationDetails`))
    );
  }

  getOrganizationStats(organizationId: number): Observable<OrganizationStats> {
    var url = environment.API_ENDPOINT + `/organization/${organizationId}/stats`;
    return this.http.get<OrganizationStats>(url, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OrganizationStats>(`getOrganizationStats`))
    );
  }

  getSubOrganizations(parentOrganizationId: number, term?: string): Observable<OrganizationDetail[]> {
    var url = `${environment.API_ENDPOINT}/organization/${parentOrganizationId}/children`;
    var args = {};
    if (term) {
      args["term"] = term
    }
    var options = this.getHttpOptions(args);
    return this.http.get<OrganizationDetail[]>(url, options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OrganizationDetail[]>(`getSubOrganizations`))
    );
  }

  getStudent(id, classId: number): Observable<Student> {
    return this.http.get<Student>(environment.API_ENDPOINT + `/student/${id}`, this.getHttpOptions({ classId: classId.toString() })).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Student>(`getStudent`))
    );
  }

  getStudents(asc: boolean, classId?: number, term?: string): Observable<Student[]> {
    return this.http.get<Student[]>(environment.API_ENDPOINT + `/student/?asc=${asc}`, this.getHttpOptions({ classId: classId ? classId.toString() : undefined, term: term ? term : undefined })).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Student[]>('getStudents'))
    );
  }

  getStudentQRCode(studentID: number, classSectionId?: number): Observable<StudentQRCode> {
    let data = {
      ClassSectionID: classSectionId
    }

    return this.http.post<StudentQRCode>(environment.API_ENDPOINT + `/student/${studentID}/qr-code`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentQRCode>('getStudentQRCode'))
    );
  }

  getStudentQRCodes(orgId: number): Observable<StudentQRCode[]> {
    return this.http.get<StudentQRCode[]>(environment.API_ENDPOINT + `/organization/${orgId}/student-qr-codes`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentQRCode[]>('getStudentQRCodes'))
    );
  }

  getStudentsByParent(accountId: number): Observable<ParentAccount> {
    return this.http.get<ParentAccount>(environment.API_ENDPOINT + '/parent/students', this.getHttpOptions({ accountID: accountId ? accountId.toString() : undefined })).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<ParentAccount>('getStudentsByParent'))
    );
  }

  getStudentProgressSummaryAllCourses(studentId: number): Observable<CourseProgressSummary[]> {
    return this.http.get<CourseProgressSummary[]>(environment.API_ENDPOINT + `/student/${studentId}/progress-summary-all-courses`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<CourseProgressSummary[]>(`getStudent`))
    );
  }

  getStudentSpeakingScores(studentId: number): Observable<StudentSpeakingScore[]> {
    return this.http.get<StudentSpeakingScore[]>(environment.API_ENDPOINT + `/student/${studentId}/speaking-scores`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentSpeakingScore[]>('getStudentSpeakingScores'))
    );
  }

  getStudentSpeakingScoreHistory(studentId: number): Observable<UnitSparcScore[]> {
    return this.http.get<UnitSparcScore[]>(environment.API_ENDPOINT + `/student/${studentId}/student-speaking-score-history`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<UnitSparcScore[]>('getStudentSpeakingScoreHistory'))
    );
  }

  getStudentWidaPredictions(studentId: number, organizationId?: number): Observable<WidaPredictionDetails> {
    let endpoint = `/student/${studentId}/wida_predictions${organizationId ? '?organizationID=' + organizationId : ''}`;
    return this.http.get<WidaPredictionDetails>(environment.API_ENDPOINT + endpoint, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<WidaPredictionDetails>('getStudentWidaPredictions'))
    );
  }

  getStudentDomainScores(studentId: number, startDate: Date | null, endDate: Date | null): Observable<StudentDomainScore[]> {
    let endpoint = `/student/${studentId}/domain-scores`;
    let startString = startDate ? `startDate=${startDate.toISOString()}` : "";
    let endString = endDate ? `endDate=${endDate.toISOString()}` : "";
    let queryStart = startDate || endDate ? "?" : "";
    let queryJoin = startDate && endDate ? "&" : "";
    let query = queryStart + startString + queryJoin + endString; 
    return this.http.get<StudentDomainScore[]>(environment.API_ENDPOINT + endpoint + query, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentDomainScore[]>('getStudentDomainScores'))
    );
  }

  getClassSpeakingScores(classId: number): Observable<StudentSpeakingScore[]> {
    return this.http.get<StudentSpeakingScore[]>(environment.API_ENDPOINT + `/class/${classId}/speaking-scores`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<StudentSpeakingScore[]>('getClassSpeakingScore'))
    );
  }

  getClassSpeakingScoreHistory(classId: number): Observable<DateSparcScore[]> {
    return this.http.get<DateSparcScore[]>(environment.API_ENDPOINT + `/class/${classId}/average-speaking-score-history`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<DateSparcScore[]>('getClassSpeakingScoreHistory'))
    );
  }

  getOrganizationSpeakingScoreHistory(organizationId: number): Observable<DateSparcScore[]> {
    return this.http.get<DateSparcScore[]>(environment.API_ENDPOINT + `/organization/${organizationId}/average-speaking-score-history`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<DateSparcScore[]>('getOrganizationSpeakingScoreHistory'))
    );
  }

  getOrganizationClassesData(organizationId: number): Observable<OrganizationClassData[]> {
    return this.http.get<OrganizationClassData[]>(environment.API_ENDPOINT + `/organization/${organizationId}/class-data`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OrganizationClassData[]>('getOrganizationClassesData'))
    );
  }

  getStudentUnitProgress(studentId: number, courseCode: string, unitNumber: number): Observable<UnitProgressDetail> {
    return this.http.get<UnitProgressDetail>(environment.API_ENDPOINT + `/student/${studentId}/progress/course/${courseCode}/unit/${unitNumber}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<UnitProgressDetail>(`getStudent`))
    );
  }

  getRawStudentUsageLimits(studentId: number): Observable<UsageLimits> {
    return this.http.get<UsageLimits>(environment.API_ENDPOINT + `/student/${studentId}/raw-usage-limits`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<UsageLimits>(`getRawStudentUsageLimits`))
    );
  }

  getRawClassUsageLimits(classId: number): Observable<UsageLimits> {
    return this.http.get<UsageLimits>(environment.API_ENDPOINT + `/class/${classId}/raw-usage-limits`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<UsageLimits>(`getRawClassUsageLimits`))
    );
  }

  getActivityList(courseCode: string, unitNumber: number): Observable<ActivityListItem[]> {
    return this.http.get<ActivityListItem[]>(environment.API_ENDPOINT + `/course/${courseCode}/unit/${unitNumber}/activity-list`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<ActivityListItem[]>(`getActivityList`))
    );
  }

  getActivityCategories(): Observable<AssignmentActivityCategory[]> {
    return this.http.get<AssignmentActivityCategory[]>(environment.API_ENDPOINT + "/activity-categories", this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AssignmentActivityCategory[]>("getActivityCategories"))
    );
  }

  getActivitiesForCategory(CategoryId: number): Observable<AssignmentActivity[]> {
    return this.http.get<AssignmentActivity[]>(environment.API_ENDPOINT + `/activity-categories/${CategoryId}/activity-list`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AssignmentActivity[]>("getActivityCategories"))
    );
  }

  getUnitsForActivity(ActivityId: string): Observable<AssignmentActivityUnit[]> {
    return this.http.get<AssignmentActivityUnit[]>(environment.API_ENDPOINT + `/activities/${ActivityId}/unit-list`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<AssignmentActivityUnit[]>("getActivityCategories"))
    );
  }

  getEEUReport(OrgID: number): Observable<EEUReport> {
    return this.http.get<EEUReport>(environment.API_ENDPOINT + `/organization/${OrgID}/stats/northstar`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<EEUReport>("getEEUReport"))
    )
  }

  removeParentFromStudent(parentId: number, studentId: number): Observable<Object> {
    return this.http.delete(environment.API_ENDPOINT + `/parent/${parentId}/student/${studentId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('removeParent'))
    );
  }

  resetPassword(password: string, token: string): Observable<any> {
    var params = {
      Password: password,
      Token: token,
    };
    return this.http.post(environment.API_ENDPOINT + '/account/password/reset', params, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleLoginError<any>('resetPassword'))
    );
  }

  searchOrganizations(term: string): Observable<Organization[]> {
    return this.http.get<Organization[]>(`${environment.API_ENDPOINT}/organization/?term=${encodeURIComponent(term)}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Organization[]>('searchOrganizations', []))
    );
  }

  searchClasses(term: string, classSectionId?: number): Observable<Class[]> {
    return this.http.get<Class[]>(`${environment.API_ENDPOINT}/class/`, this.getHttpOptions({ term: term, classSectionId: (classSectionId ? classSectionId.toString() : undefined) })).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Class[]>('searchClasses', []))
    );
  }

  searchExistingStudents(classId: number, term: string): Observable<Student[]> {
    return this.http.get<Student[]>(`${environment.API_ENDPOINT}/class/${classId}/related-students?term=${encodeURIComponent(term)}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Student[]>('searchExistingStudents', []))
    );
  }

  searchAccountsByEmail(term: string): Observable<VisibleAccount[]> {
    return this.http.get<VisibleAccount[]>(`${environment.API_ENDPOINT}/search-accounts-email/?term=${encodeURIComponent(term)}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<VisibleAccount[]>('searchAccountsByEmail', []))
    );
  }

  sendAccountWelcomeEmail(accountId: number): Observable<any> {
    var domain = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
    var params = {
      Domain: domain,
    };
    return this.http.post(environment.API_ENDPOINT + `/account/${accountId}/welcome-email`, params, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('sendAccountWelcomeEmail'))
    );
  }

  sendResetPasswordEmail(email: string): Observable<any> {
    var domain = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
    var params = {
      Domain: domain,
      Email: email,
    };
    return this.http.post(environment.API_ENDPOINT + '/account/password/reset/email', params, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('sendResetPasswordEmail'))
    );
  }

  setClassUsageLimits(usageLimits: ClassUsageLimits): Observable<any> {
    return this.http.put(environment.API_ENDPOINT + `/class/${usageLimits.ClassID}/usage-limit`, usageLimits, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('setDailyUsageLimit'))
    );
  }

  setRawStudentUsageLimits(studentId: number, usageLimits: UsageLimits): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + `/student/${studentId}/raw-usage-limits`, usageLimits, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('setRawStudentUsageLimit'))
    );
  }

  setRawClassUsageLimits(classId: number, usageLimits: UsageLimits): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + `/class/${classId}/raw-usage-limits`, usageLimits, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('setClassStudentUsageLimit'))
    );
  }

  updateAccount(account: Account): Observable<any> {
    return this.http.put(environment.API_ENDPOINT + `/account/${account.ID}`, account, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('updateAccount'))
    );
  }

  updateClass(theClass: Class): Observable<any> {
    return this.http.put(environment.API_ENDPOINT + `/class/${theClass.ID}`, theClass, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('updateClass'))
    );
  }

  updateOrganization(organization: Organization): Observable<any> {
    return this.http.put(environment.API_ENDPOINT + `/organization/${organization.ID}`, organization, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('updateOrganization'))
    );
  }

  addStudentToClass(studentID: number, classId: number, enforceUniqueName: boolean, deleteFromOtherClasses: boolean): Observable<any> {
    var data = {
      EnforceUniqueName: true,
      DeleteFromOtherClasses: deleteFromOtherClasses
    };
    return this.http.post(environment.API_ENDPOINT + `/student/${studentID}/class/${classId}`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('addStudentToClass'))
    );
  }

  deleteStudentFromClass(studentID: number, classId: number): Observable<any> {
    return this.http.delete(environment.API_ENDPOINT + `/student/${studentID}/class/${classId}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('deleteStudentFromClass'))
    );
  }

  findStudentsByAccount(term: string): Observable<any> {
    return this.http.get(environment.API_ENDPOINT + `/account/students`, this.getHttpOptions({ term: term ? term : undefined })).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('findStudentsByAccount', []))
    );
  }

  updateStudent(student: Student): Observable<any> {
    if (!student.PassCode) {
      student.PassCode = '';
    }
    return this.http.put(environment.API_ENDPOINT + `/student/${student.ID}`, student, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('updateStudent'))
    );
  }

  updateTeacher(teacher: Teacher): Observable<any> {
    var account = new Account();
    account.ID = teacher.AccountID;
    account.Name = teacher.Name;
    account.Email = teacher.Email;
    account.ExternalID = teacher.ExternalID;
    return this.updateAccount(account);
  }

  verifyResetPasswordToken(token: string): Observable<any> {
    const options = {
      params: new HttpParams().set('token', token)
    };
    return this.http.get(environment.API_ENDPOINT + '/account/password/reset/verify', options).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<any>('resetPassword'))
    );
  }

  getOrgUsageReport(orgID: number, startDate: string, endDate: string): Observable<any> {
    let data = {
      startDate: startDate,
      endDate: endDate
    };
    return this.http.post<OrgUsageReport[]>(environment.API_ENDPOINT + `/organization/${orgID}/activity-report`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OrgUsageReport[]>('getOrgUsageReport'))
    );
  }

  getFullOrgUsageReport(orgID: number, startDate: string, endDate: string, includeNoActivity: boolean = true): Observable<any> {
    let data = {
      startDate: startDate,
      endDate: endDate,
      includeNoActivity: includeNoActivity
    };
    return this.http.post(environment.API_ENDPOINT + `/organization/${orgID}/full-activity-report`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getOrgUsageReport'))
    );
  }

  getCarrReport(orgID: number, startDate: string, endDate: string): Observable<any> {
    let data = {
      startDate: startDate,
      endDate: endDate,
      minActivitySeconds: 0,
      maxActivitySeconds: 0
    };
    return this.http.post(environment.API_ENDPOINT + `/organization/${orgID}/carr-report`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getCarrReport'))
    );
  }

  getLicenseReport(orgID: number, startDate: string, endDate: string): Observable<any> {
    let data = {
      startDate: startDate,
      endDate: endDate
    };
    return this.http.post(environment.API_ENDPOINT + `/organization/${orgID}/license-report`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getLicenseReport'))
    );
  }

  getTopSchoolsReport(orgID: number, startDate: string, endDate: string): Observable<any> {
    let hasStart = startDate != null;
    let hasEnd = endDate != null;
    let startText = hasStart ? "StartDate=" + encodeURIComponent(new Date(startDate).toISOString()) : "";
    let endText = hasEnd ? "EndDate=" + encodeURIComponent(new Date(endDate).toISOString()) : "";
    let hasQuery = hasStart || hasEnd;
    let queryText = startText + (hasStart && hasEnd ? "&" : "") + endText

    return this.http.get<string>(environment.API_ENDPOINT + `/support-toolbox/top-schools-report/${orgID}` + (hasQuery ? "?" + queryText : ""), this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getTopSchoolsReport'))
    );
  }

  getReportWithDatesIncludeNoActivityOld(orgID: number, startDate: string, endDate: string): Observable<any> {
    let hasStart = startDate != null;
    let hasEnd = endDate != null;
    let startText = hasStart ? "StartDate=" + encodeURIComponent(new Date(startDate).toISOString()) : "";
    let endText = hasEnd ? "EndDate=" + encodeURIComponent(new Date(endDate).toISOString()) : "";
    let hasQuery = hasStart || hasEnd;
    let queryText = startText + (hasStart && hasEnd ? "&" : "") + endText

    return this.http.get<string>(environment.API_ENDPOINT + `/support-toolbox/report-with-dates-include-no-activity-old/${orgID}` + (hasQuery ? "?" + queryText : ""), this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getReportWithDatesIncludeNoActivityOld'))
    );
  }

  getSupportToolboxReportWithDates(orgID: number, startDate: string, endDate: string): Observable<any> {
    let hasStart = startDate != null;
    let hasEnd = endDate != null;
    let startText = hasStart ? "StartDate=" + encodeURIComponent(new Date(startDate).toISOString()) : "";
    let endText = hasEnd ? "EndDate=" + encodeURIComponent(new Date(endDate).toISOString()) : "";
    let hasQuery = hasStart || hasEnd;
    let queryText = startText + (hasStart && hasEnd ? "&" : "") + endText

    return this.http.get<string>(environment.API_ENDPOINT + `/support-toolbox/report-with-dates/${orgID}` + (hasQuery ? "?" + queryText : ""), this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getReportWithDatesIncludeNoActivityOld'))
    );
  }

  getSupportToolboxAggregateLicenseReport(orgID: number, startDate: string, endDate: string): Observable<any> {
    let hasStart = startDate != null;
    let hasEnd = endDate != null;
    let startText = hasStart ? "StartDate=" + encodeURIComponent(new Date(startDate).toISOString()) : "";
    let endText = hasEnd ? "EndDate=" + encodeURIComponent(new Date(endDate).toISOString()) : "";
    let hasQuery = hasStart || hasEnd;
    let queryText = startText + (hasStart && hasEnd ? "&" : "") + endText

    return this.http.get<string>(environment.API_ENDPOINT + `/support-toolbox/aggregate-license-report/${orgID}` + (hasQuery ? "?" + queryText : ""), this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getSupportToolboxAggregateLicenseReport'))
    );
  }

  getGenericReport(orgID: number): Observable<any> {
    let data = {
      startDate: null,
      endDate: null,
      includeNoActivity: false
    };

    return this.http.post<string>(environment.API_ENDPOINT + `/organization/${orgID}/full-activity-report`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('getReportWithDatesIncludeNoActivityOld'))
    );
  }

  getAdminAccountsReport(orgID: number): Observable<any> {
    return this.http.get<string>(environment.API_ENDPOINT + `/support-toolbox/admin-accounts-report/${orgID}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<string>('getAdminAccountsReport'))
    );
  }

  getStaffAccountsReport(orgID: number): Observable<any> {
    return this.http.get<string>(environment.API_ENDPOINT + `/support-toolbox/staff-accounts-report/${orgID}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<string>('getStaffAccountsReport'))
    );
  }

  scrubOrg(orgID: number): Observable<any> {
    let data = {};

    return this.http.post<string>(environment.API_ENDPOINT + `/support-toolbox/scrub-org/${orgID}`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<string>('scrubOrg'))
    );
  }

  sendCertificateEmail(email: CertificateEmail): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + "/email-certificate", email, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<CertificateEmail>('sendCertificateEmail'))
    );
  }

  sendSupportEmail(email: SupportEmail): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + "/email-support", email, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<CertificateEmail>('sendSupportEmail'))
    );
  }

  batchReassignStudentLevels(updates: StudentLevelReassignment[], classID: number) {
    return this.http.post(environment.API_ENDPOINT + `/class/${classID}/batch-level-update`, updates, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('batchReassignStudentLevels'))
    );
  }

  getWonDeal(dealID: number): Observable<Deal> {
    return this.http.get<Deal>(environment.API_ENDPOINT + `/deal/${dealID}`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Deal>('getWonDeal'))
    );
  }

  createAccountFromDeal(dealID: number, newAccount: Account): Observable<Account> {
    return this.http.post<Account>(environment.API_ENDPOINT + `/deal/${dealID}`, newAccount, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Account>('createAccountFromDeal'))
    );
  }

  getOnboardingStatus(orgID: number): Observable<OnboardingStatus> {
    return this.http.get<OnboardingStatus>(environment.API_ENDPOINT + `/organization/${orgID}/onboarding-status`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OnboardingStatus>('getOnboardingStatus'))
    );
  }

  setOnboardingStatus(orgID: number, status: OnboardingStatus): Observable<OnboardingStatus> {
    return this.http.put<OnboardingStatus>(environment.API_ENDPOINT + `/organization/${orgID}/onboarding-status`, status, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OnboardingStatus>('setOnboardingStatus'))
    );
  }

  assignOnboardingStep(orgID: number, assignment: any): Observable<OnboardingStatus> {
    return this.http.post<OnboardingStatus>(environment.API_ENDPOINT + `/organization/${orgID}/onboarding-status/assign`, assignment, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<OnboardingStatus>('assignOnboardingStep'))
    );
  }

  importOrganization(orgId: number, data: OrgImportData): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + `/organization/${orgId}/import`, data, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('importOrganization'))
    );
  }

  linkCleverOrganization(orgId: number, cleverId: string): Observable<any> {
    return this.http.post(environment.API_ENDPOINT + `/organization/${orgId}/link-clever`, { CleverId: cleverId }, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError('linkCleverOrganization'))
    );
  }

  getSchoolRoster(schoolId: number): Observable<Roster> {
    return this.http.get<Roster>(environment.API_ENDPOINT + `/organization/${schoolId}/roster`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<Roster>('getSchoolRoster'))
    );
  }

  getLastSync(OrgId: number): Observable<SyncLog> {
    return this.http.get<SyncLog>(environment.API_ENDPOINT + `/organization/${OrgId}/last-sync`, this.getHttpOptions()).pipe(
      tap(r => this.checkForErrorInResponse(r)),
      catchError(this.handleError<SyncLog>('getLastSync'))
    );
  }

  private log(message: string) {
    console.log('DataService: ' + message);
  }

  /**
  * Handle Http operation that failed.
  * Let the app continue.
  * @param operation - name of the operation that failed
  * @param result - optional value to return as the observable result
  */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      if (this.authService && this.authService.isSessionExpired()) {
        this.authService.logout();
        this.router.navigate(['/login'], { queryParams: { expired: 1 } });
        return of(result as T);
      }
      let message = error.message;
      if (error.message.includes("Task timed out after")) {
        message = "The request timed out. This is most likely caused by an import or report taking too long. If trying the request again doesn't work then contact support.";
      }
      swal.fire('Oops!', message, 'error');
      this.log(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /**
  * Handle Http operation that failed before login.
  * Let the app continue.
  * @param operation - name of the operation that failed
  * @param result - optional value to return as the observable result
  */
  private handleLoginError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      swal.fire('Oops!', '' + error, 'error');
      this.log(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  promptDelete(text: string, confirmButtonText?: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      swal.fire({
        title: 'Are you sure?',
        text: text,
        type: 'warning',
        confirmButtonText: confirmButtonText ? confirmButtonText : 'Delete',
        showConfirmButton: true,
        showCancelButton: true,
        reverseButtons: true,
        customClass: {
          confirmButton: 'swal-button--danger',
        }
      })
        .then((res) => {
          if (res.value) {
            resolve(res.value);
          }
        });
    });
  }
}
